/**
 * Catalog picker — choose items to add to a BOM or to an issuance.
 *
 * Functional spec: the catalog pane inside `qt_poc/bom_qt.py`, which
 * `IssuancePickerDialog` in `qt_poc/customers_qt.py` mirrors deliberately
 * (`מק"ט יצרן · תיאור · יצרן · משפחה · מחיר נטו/יח'`).
 *
 * Items already on the BOM are excluded rather than shown greyed: adding a
 * duplicate line is meaningless here, and the desktop treats a second add of
 * the same item as a no-op.
 *
 * The caller supplies the wording, because what an added row *becomes*
 * differs: a BOM gets one line per item, an issuance gets one line per
 * component of that item.
 */
import { useMemo, useState } from 'react'
import {
  Button, Checkbox, Group, Select, Stack, Text, TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'

import { api } from '../api/client'
import type { ItemRow, MasterData } from '../api/types'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { EM_DASH, fmtMoney } from '../format'

interface Props {
  opened: boolean
  onClose: () => void
  onPick: (items: ItemRow[]) => void
  excludeIds?: number[]
  title?: string
  confirmLabel?: string
}

export function ItemPicker({
  opened, onClose, onPick, excludeIds = [],
  title = 'הוספת פריטים ל-BOM', confirmLabel = 'הוסף ל-BOM',
}: Props) {
  const [search, setSearch] = useState('')
  const [debounced] = useDebouncedValue(search, 300)
  const [manufacturer, setManufacturer] = useState<string | null>(null)
  const [chosen, setChosen] = useState<Set<number>>(new Set())

  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
    enabled: opened,
  })

  const items = useQuery({
    queryKey: ['items', debounced, manufacturer, null, null],
    queryFn: () => {
      const params = new URLSearchParams()
      if (debounced) params.set('q', debounced)
      if (manufacturer) params.set('manufacturer', manufacturer)
      const qs = params.toString()
      return api.get<ItemRow[]>(`/items${qs ? `?${qs}` : ''}`)
    },
    enabled: opened,
  })

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds])
  const rows = useMemo(
    () => (items.data ?? []).filter((i) => i.id != null && !excluded.has(i.id)),
    [items.data, excluded],
  )

  const columns = useMemo<ColumnDef<ItemRow, any>[]>(
    () => [
      {
        id: 'pick',
        header: '',
        cell: (c) => (
          <Checkbox
            checked={chosen.has(c.row.original.id!)}
            onChange={() =>
              setChosen((prev) => {
                const next = new Set(prev)
                const id = c.row.original.id!
                next.has(id) ? next.delete(id) : next.add(id)
                return next
              })
            }
            aria-label="בחר פריט"
            size="md"
          />
        ),
        // Widths are declared because this grid lists the whole catalog and so
        // windows itself; see ColumnMeta.width in DataGrid.
        meta: { width: 46 },
      },
      { accessorKey: 'name', header: 'מק"ט יצרן', meta: { code: true, width: 140 } },
      {
        accessorKey: 'description',
        header: 'תיאור',
        meta: { width: 300 },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      { accessorKey: 'manufacturer', header: 'יצרן', meta: { width: 110 } },
      {
        accessorKey: 'family',
        header: 'משפחה',
        meta: { width: 120 },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      {
        id: 'price',
        header: "מחיר נטו/יח'",
        meta: { numeric: true, width: 120 },
        accessorFn: (r) => r.effective_price,
        // `effective_price` is null for a viewer (DP-01), same as everywhere
        // else it appears — but this picker is only ever opened from a
        // `canEdit`-gated button (BomDetail, IssuanceEditor), so a viewer
        // never actually sees this grid.
        cell: (c) => {
          const price = c.row.original.effective_price
          return price != null && price > 0
            ? fmtMoney(price, c.row.original.price_currency)
            : EM_DASH
        },
      },
    ],
    [chosen],
  )

  function confirm() {
    onPick(rows.filter((r) => chosen.has(r.id!)))
    setChosen(new Set())
  }

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="xl">
      <Stack>
        <Group>
          <TextInput
            label="חיפוש"
            placeholder="חיפוש…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            label="יצרן"
            clearable
            clearButtonProps={{ "aria-label": "נקה", size: "md" }}
            searchable
            placeholder="הכל"
            data={master.data?.manufacturers ?? []}
            value={manufacturer}
            onChange={setManufacturer}
            style={{ minWidth: 180 }}
          />
        </Group>

        <DataGrid
          data={rows}
          columns={columns}
          loading={items.isLoading}
          empty="לא נמצאו פריטים"
          maxHeight="50vh"
          stickyFirstColumn={false}
        />

        <Group justify="space-between">
          <Text size="sm" c="dimmed">{chosen.size} נבחרו</Text>
          <Group>
            <Button variant="default" onClick={onClose}>ביטול</Button>
            <Button onClick={confirm} disabled={!chosen.size}>{confirmLabel}</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  )
}
